// src/components/admin/UserManagementPanel.tsx
// DROP 44 — Full User Management: CRUD + RBAC Role Assignment + Password Reset + Sessions
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Edit2, Trash2, Key, Power, PowerOff,
  Search, Shield, Eye, EyeOff, X, CheckCircle,
  AlertTriangle, Loader2, RefreshCw, LogOut, Clock
} from 'lucide-react';

const C = {
  bg: '#070D0A', surface: '#0E1812', card: '#121E16',
  border: '#1C2E22', gold: '#C9A84C', goldLt: '#E2C97E',
  text: '#EDE8DC', muted: 'rgba(237,232,220,0.5)',
  dim: 'rgba(237,232,220,0.25)', green: '#0F5132',
  red: '#ef4444', blue: '#60a5fa', amber: '#f59e0b',
};

// ─── RBAC Role Definitions ───────────────────────────────────────────────────
const PORTAL_ROLES: Record<string, { label: string; roles: { value: string; label: string; desc: string }[] }> = {
  'Board of Trustees': {
    label: '🏛️ Board of Trustees',
    roles: [
      { value: 'BOARD_CHAIR',   label: 'Board Chair',   desc: 'Full BOT authority — approve budgets & resolutions' },
      { value: 'BOARD_TRUSTEE', label: 'Board Trustee', desc: 'Review & approve — read access to all BOT content' },
    ],
  },
  'CWC': {
    label: '⚙️ Central Working Committee',
    roles: [
      { value: 'CWC_TREASURER', label: 'CWC Treasurer', desc: 'Full financial ops — budget, accounting, reports' },
      { value: 'CWC_SECRETARY', label: 'CWC Secretary', desc: 'Documents, communications, news publishing' },
      { value: 'CWC_MEMBER',    label: 'CWC Member',    desc: 'CWC portal access — budget view only' },
    ],
  },
  'LOC': {
    label: '📋 Local Organizing Committee',
    roles: [
      { value: 'LOC_CHAIR',   label: 'LOC Chair',   desc: 'LOC leadership — submit budgets, view reports' },
      { value: 'LOC_MEMBER',  label: 'LOC Member',  desc: 'LOC portal access — submit inputs only' },
    ],
  },
  'Finance': {
    label: '💰 Finance',
    roles: [
      { value: 'ACCOUNTANT', label: 'Accountant', desc: 'Full accounting, budget, reports, raffle management' },
    ],
  },
  'Operations': {
    label: '🎪 Operations',
    roles: [
      { value: 'FESTIVAL_COORDINATOR', label: 'Festival Coordinator', desc: 'Programme, registrations, community admin, media' },
      { value: 'SECRETARY',            label: 'Secretary',            desc: 'Documents, communications, news publishing' },
      { value: 'MEDIA_MANAGER',        label: 'Media Manager',        desc: 'Gallery, livestream, documents, news' },
      { value: 'REGISTRAR',            label: 'Registrar',            desc: 'Event registrations & voting only' },
    ],
  },
  'Platform': {
    label: '👑 Platform',
    roles: [
      { value: 'PLATFORM_ADMIN', label: 'Platform Admin', desc: 'ALL permissions — full system control' },
      { value: 'VIEWER',         label: 'Viewer',         desc: 'Read-only — no staff permissions' },
    ],
  },
};

const ALL_ROLES = Object.values(PORTAL_ROLES).flatMap(g => g.roles);

const ROLE_BADGE: Record<string, { color: string; bg: string; emoji: string }> = {
  PLATFORM_ADMIN:      { color: '#C9A84C', bg: 'rgba(201,168,76,0.15)',   emoji: '👑' },
  BOARD_CHAIR:         { color: '#818cf8', bg: 'rgba(129,140,248,0.15)',  emoji: '🏛️' },
  BOARD_TRUSTEE:       { color: '#a78bfa', bg: 'rgba(167,139,250,0.15)',  emoji: '⚖️' },
  CWC_TREASURER:       { color: '#34d399', bg: 'rgba(52,211,153,0.15)',   emoji: '💰' },
  CWC_SECRETARY:       { color: '#60a5fa', bg: 'rgba(96,165,250,0.15)',   emoji: '📝' },
  CWC_MEMBER:          { color: '#6ee7b7', bg: 'rgba(110,231,183,0.15)',  emoji: '⚙️' },
  LOC_CHAIR:           { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)',   emoji: '📋' },
  LOC_MEMBER:          { color: '#fcd34d', bg: 'rgba(252,211,77,0.15)',   emoji: '🗂️' },
  ACCOUNTANT:          { color: '#22c55e', bg: 'rgba(34,197,94,0.15)',    emoji: '🧾' },
  FESTIVAL_COORDINATOR:{ color: '#f97316', bg: 'rgba(249,115,22,0.15)',   emoji: '🎪' },
  SECRETARY:           { color: '#38bdf8', bg: 'rgba(56,189,248,0.15)',   emoji: '✍️' },
  MEDIA_MANAGER:       { color: '#e879f9', bg: 'rgba(232,121,249,0.15)',  emoji: '🎬' },
  REGISTRAR:           { color: '#4ade80', bg: 'rgba(74,222,128,0.15)',   emoji: '📌' },
  VIEWER:              { color: '#94a3b8', bg: 'rgba(148,163,184,0.15)',  emoji: '👁️' },
};

// ─── Types ───────────────────────────────────────────────────────────────────
interface PlatformUser {
  id: string;
  name: string;
  email: string;
  role: string;
  systemRole: string;
  portal: string;
  permissions: string[];
  active: boolean;
  createdAt: string;
  lastLogin: string | null;
  firstLogin?: boolean;
  passwordChangedAt?: string | null;
}

interface UserSession {
  id: string;
  userId?: string;
  email: string;
  role: string;
  loginAt: string;
  ip?: string;
}

// ─── Empty form state ─────────────────────────────────────────────────────────
const emptyForm = () => ({
  name: '', email: '', password: '', confirmPassword: '',
  portal: 'Operations', systemRole: 'FESTIVAL_COORDINATOR',
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
const authHdr = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || ''}`,
});

const fmt = (iso: string | null) => iso
  ? new Date(iso).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
  : 'Never';

// ─── Sub-components ───────────────────────────────────────────────────────────
function RoleBadge({ role }: { role: string }) {
  const b = ROLE_BADGE[role] || { color: C.muted, bg: 'rgba(237,232,220,0.05)', emoji: '👤' };
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:4,
      padding:'2px 8px', borderRadius:12, fontSize:11, fontWeight:600,
      color: b.color, background: b.bg,
    }}>
      {b.emoji} {ALL_ROLES.find(r => r.value === role)?.label || role}
    </span>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:1000,
      background:'rgba(0,0,0,0.8)', backdropFilter:'blur(4px)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:16,
    }}>
      <div style={{
        background: C.card, border:`1px solid ${C.border}`, borderRadius:16,
        width:'100%', maxWidth:560, maxHeight:'90vh', overflow:'auto',
        boxShadow:'0 24px 64px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'16px 20px', borderBottom:`1px solid ${C.border}`,
          position:'sticky', top:0, background: C.card, zIndex:1,
        }}>
          <h3 style={{ fontFamily:'Cinzel, serif', fontSize:15, color:C.gold, margin:0 }}>{title}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding:20 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:10, fontWeight:700, letterSpacing:1,
        textTransform:'uppercase', color:C.muted, marginBottom:5 }}>
        {label}{required && <span style={{ color:C.red }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width:'100%', padding:'9px 12px', borderRadius:8, fontSize:13,
  background:'#0a1410', border:`1px solid ${C.border}`,
  color:C.text, outline:'none', boxSizing:'border-box',
};

const btnStyle = (variant: 'primary'|'danger'|'ghost'|'amber' = 'primary'): React.CSSProperties => ({
  display:'inline-flex', alignItems:'center', gap:6,
  padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600,
  background: variant === 'primary' ? C.green
    : variant === 'danger' ? 'rgba(239,68,68,0.15)'
    : variant === 'amber' ? 'rgba(245,158,11,0.15)'
    : 'transparent',
  color: variant === 'primary' ? C.goldLt
    : variant === 'danger' ? C.red
    : variant === 'amber' ? C.amber
    : C.muted,
  border: variant === 'danger' ? `1px solid rgba(239,68,68,0.3)`
    : variant === 'amber' ? `1px solid rgba(245,158,11,0.3)`
    : variant === 'ghost' ? `1px solid ${C.border}`
    : 'none',
});

// ─── Main Component ────────────────────────────────────────────────────────────
interface Props {
  currentUser?: { email?: string; systemRole?: string; role?: string } | null;
}

export default function UserManagementPanel({ currentUser }: Props) {
  const [tab, setTab] = useState<'users'|'sessions'>('users');
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPortal, setFilterPortal] = useState('All');
  const [filterActive, setFilterActive] = useState<'all'|'active'|'inactive'>('all');

  // Modals
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState<PlatformUser | null>(null);
  const [resetUser, setResetUser] = useState<PlatformUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<PlatformUser | null>(null);

  // Form state
  const [form, setForm] = useState(emptyForm());
  const [newPw, setNewPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'ok'|'err' } | null>(null);

  const showToast = (msg: string, type: 'ok'|'err' = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/admin/users', { headers: authHdr() });
      const d = await r.json();
      if (d.success) setUsers(d.users || []);
    } catch { showToast('Failed to load users', 'err'); }
    finally { setLoading(false); }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/sessions', { headers: authHdr() });
      const d = await r.json();
      if (d.success) setSessions(d.sessions || []);
    } catch { /* sessions may not exist */ }
  }, []);

  useEffect(() => { loadUsers(); loadSessions(); }, [loadUsers, loadSessions]);

  // ── Role options for selected portal ────────────────────────────────────────
  const rolesForPortal = (portal: string) =>
    PORTAL_ROLES[portal]?.roles || PORTAL_ROLES['Operations'].roles;

  // ── Add User ────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!form.name || !form.email || !form.password) {
      showToast('Name, email and password are required', 'err'); return;
    }
    if (form.password !== form.confirmPassword) {
      showToast('Passwords do not match', 'err'); return;
    }
    if (form.password.length < 6) {
      showToast('Password must be at least 6 characters', 'err'); return;
    }
    setSaving(true);
    try {
      const r = await fetch('/api/admin/users', {
        method:'POST', headers: authHdr(),
        body: JSON.stringify({
          name: form.name, email: form.email, password: form.password,
          role: form.systemRole, systemRole: form.systemRole,
          portal: form.portal, permissions: [],
        }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ ${form.name} added successfully`);
        setShowAdd(false); setForm(emptyForm()); loadUsers();
      } else showToast(d.error || 'Failed to add user', 'err');
    } catch { showToast('Network error', 'err'); }
    finally { setSaving(false); }
  };

  // ── Edit User ────────────────────────────────────────────────────────────────
  const handleEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const r = await fetch(`/api/admin/users/${editUser.id}`, {
        method:'PUT', headers: authHdr(),
        body: JSON.stringify({
          name: form.name, email: form.email,
          role: form.systemRole, systemRole: form.systemRole,
          portal: form.portal,
        }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ ${form.name} updated`);
        setEditUser(null); setForm(emptyForm()); loadUsers();
      } else showToast(d.error || 'Update failed', 'err');
    } catch { showToast('Network error', 'err'); }
    finally { setSaving(false); }
  };

  // ── Reset Password ────────────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!resetUser || !newPw) { showToast('Enter new password', 'err'); return; }
    if (newPw.length < 6) { showToast('Password min 6 characters', 'err'); return; }
    setSaving(true);
    try {
      const r = await fetch(`/api/admin/users/${resetUser.id}/reset-password`, {
        method:'POST', headers: authHdr(),
        body: JSON.stringify({ newPassword: newPw }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ Password reset for ${resetUser.name}`);
        setResetUser(null); setNewPw('');
      } else showToast(d.error || 'Reset failed', 'err');
    } catch { showToast('Network error', 'err'); }
    finally { setSaving(false); }
  };

  // ── Toggle Active ────────────────────────────────────────────────────────────
  const handleToggleActive = async (u: PlatformUser) => {
    try {
      const r = await fetch(`/api/admin/users/${u.id}/toggle-active`, {
        method:'POST', headers: authHdr(),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ ${u.name} ${d.active ? 'activated' : 'deactivated'}`);
        loadUsers();
      } else showToast(d.error || 'Failed', 'err');
    } catch { showToast('Network error', 'err'); }
  };

  // ── Delete User ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteUser) return;
    setSaving(true);
    try {
      const r = await fetch(`/api/admin/users/${deleteUser.id}`, {
        method:'DELETE', headers: authHdr(),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`✅ ${deleteUser.name} deleted`);
        setDeleteUser(null); loadUsers();
      } else showToast(d.error || 'Delete failed', 'err');
    } catch { showToast('Network error', 'err'); }
    finally { setSaving(false); }
  };

  // ── Force Logout Session ──────────────────────────────────────────────────────
  const handleForceLogout = async (sessionId: string, email: string) => {
    try {
      const r = await fetch(`/api/admin/sessions/${sessionId}`, {
        method:'DELETE', headers: authHdr(),
      });
      const d = await r.json();
      if (d.success) { showToast(`✅ Session terminated for ${email}`); loadSessions(); }
      else showToast(d.error || 'Failed', 'err');
    } catch { showToast('Network error', 'err'); }
  };

  // ── Filtered users ────────────────────────────────────────────────────────────
  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || (u.systemRole||u.role||'').toLowerCase().includes(q);
    const matchPortal = filterPortal === 'All' || u.portal === filterPortal;
    const matchActive = filterActive === 'all' || (filterActive === 'active' ? u.active : !u.active);
    return matchSearch && matchPortal && matchActive;
  });

  const openEdit = (u: PlatformUser) => {
    setEditUser(u);
    setForm({ name:u.name, email:u.email, password:'', confirmPassword:'', portal:u.portal||'Operations', systemRole:u.systemRole||u.role||'VIEWER' });
  };

  // ─── User Form (shared Add/Edit) ───────────────────────────────────────────
  const UserForm = ({ isEdit }: { isEdit?: boolean }) => (
    <>
      <Field label="Full Name" required>
        <input style={inputStyle} value={form.name} placeholder="e.g. Amos Osagie"
          onChange={e => setForm(f => ({...f, name:e.target.value}))} />
      </Field>
      <Field label="Email Address" required>
        <input style={inputStyle} value={form.email} type="email" placeholder="user@uzairue.gov"
          onChange={e => setForm(f => ({...f, email:e.target.value}))} />
      </Field>
      {!isEdit && (
        <>
          <Field label="Password" required>
            <div style={{ position:'relative' }}>
              <input style={{...inputStyle, paddingRight:36}} value={form.password}
                type={showPw ? 'text' : 'password'} placeholder="Min. 6 characters"
                onChange={e => setForm(f => ({...f, password:e.target.value}))} />
              <button type="button" onClick={() => setShowPw(p=>!p)} style={{
                position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                background:'none', border:'none', color:C.muted, cursor:'pointer',
              }}>
                {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
              </button>
            </div>
          </Field>
          <Field label="Confirm Password" required>
            <input style={inputStyle} value={form.confirmPassword}
              type={showPw ? 'text' : 'password'} placeholder="Repeat password"
              onChange={e => setForm(f => ({...f, confirmPassword:e.target.value}))} />
          </Field>
        </>
      )}

      <Field label="Portal / Department" required>
        <select style={inputStyle} value={form.portal}
          onChange={e => {
            const portal = e.target.value;
            const firstRole = PORTAL_ROLES[portal]?.roles[0]?.value || 'VIEWER';
            setForm(f => ({...f, portal, systemRole: firstRole}));
          }}>
          {Object.keys(PORTAL_ROLES).map(p => (
            <option key={p} value={p}>{PORTAL_ROLES[p].label}</option>
          ))}
        </select>
      </Field>

      <Field label="Role" required>
        <select style={inputStyle} value={form.systemRole}
          onChange={e => setForm(f => ({...f, systemRole:e.target.value}))}>
          {rolesForPortal(form.portal).map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        {/* Role description */}
        <div style={{ marginTop:6, padding:'6px 10px', background:'rgba(201,168,76,0.06)',
          border:`1px solid rgba(201,168,76,0.15)`, borderRadius:6, fontSize:11, color:C.muted }}>
          {rolesForPortal(form.portal).find(r=>r.value===form.systemRole)?.desc || ''}
        </div>
      </Field>

      <div style={{ display:'flex', gap:8, marginTop:20, justifyContent:'flex-end' }}>
        <button style={btnStyle('ghost')} onClick={() => { isEdit ? setEditUser(null) : setShowAdd(false); setForm(emptyForm()); }}>
          Cancel
        </button>
        <button style={btnStyle('primary')} onClick={isEdit ? handleEdit : handleAdd} disabled={saving}>
          {saving ? <Loader2 size={13} style={{ animation:'spin 1s linear infinite' }}/> : isEdit ? <Edit2 size={13}/> : <Plus size={13}/>}
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add User'}
        </button>
      </div>
    </>
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background:C.bg, minHeight:'100vh', padding:24, fontFamily:'Plus Jakarta Sans, sans-serif', color:C.text }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position:'fixed', top:20, right:20, zIndex:2000,
          background: toast.type==='ok' ? '#0F5132' : '#7f1d1d',
          border:`1px solid ${toast.type==='ok' ? C.gold : C.red}`,
          borderRadius:10, padding:'10px 16px', color:C.text, fontSize:13,
          display:'flex', alignItems:'center', gap:8, boxShadow:'0 8px 24px rgba(0,0,0,0.4)',
        }}>
          {toast.type==='ok' ? <CheckCircle size={14} color={C.gold}/> : <AlertTriangle size={14} color={C.red}/>}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontFamily:'Cinzel, serif', fontSize:22, color:C.gold, margin:0 }}>
            👥 User Management
          </h1>
          <p style={{ color:C.muted, fontSize:13, margin:'4px 0 0' }}>
            Platform Admin — full CRUD control over users, roles &amp; sessions
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button style={btnStyle('ghost')} onClick={() => { loadUsers(); loadSessions(); }}>
            <RefreshCw size={13}/> Refresh
          </button>
          <button style={btnStyle('primary')} onClick={() => { setForm(emptyForm()); setShowAdd(true); }}>
            <Plus size={13}/> Add User
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:12, marginBottom:24 }}>
        {[
          { label:'Total Users',  value: users.length,                    color:C.gold },
          { label:'Active',       value: users.filter(u=>u.active).length, color:'#22c55e' },
          { label:'Inactive',     value: users.filter(u=>!u.active).length,color:C.red },
          { label:'Active Sessions', value: sessions.length,              color:C.blue },
        ].map(s => (
          <div key={s.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 16px' }}>
            <div style={{ fontSize:22, fontWeight:700, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:11, color:C.muted }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div style={{ display:'flex', gap:4, borderBottom:`1px solid ${C.border}`, marginBottom:20 }}>
        {([
          { key:'users',    label:'👥 Users & Roles' },
          { key:'sessions', label:'🔐 Active Sessions' },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:'8px 16px', borderRadius:'8px 8px 0 0', border:'none', cursor:'pointer',
            background: tab===t.key ? C.green : 'transparent',
            color: tab===t.key ? C.goldLt : C.muted,
            fontSize:13, fontWeight: tab===t.key ? 600 : 400,
            borderBottom: tab===t.key ? `2px solid ${C.gold}` : '2px solid transparent',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── USERS TAB ── */}
      {tab === 'users' && (
        <>
          {/* Filters */}
          <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
            <div style={{ position:'relative', flex:1, minWidth:200 }}>
              <Search size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.muted }}/>
              <input style={{...inputStyle, paddingLeft:32}} placeholder="Search users by name, email or role..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select style={{...inputStyle, width:'auto', minWidth:160}} value={filterPortal}
              onChange={e => setFilterPortal(e.target.value)}>
              <option value="All">All Portals</option>
              {Object.keys(PORTAL_ROLES).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select style={{...inputStyle, width:'auto'}} value={filterActive}
              onChange={e => setFilterActive(e.target.value as 'all'|'active'|'inactive')}>
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign:'center', padding:60, color:C.muted }}>
              <Loader2 size={28} style={{ animation:'spin 1s linear infinite', margin:'0 auto 12px', display:'block', color:C.gold }}/>
              Loading users...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:60, color:C.dim }}>
              <Users size={32} style={{ margin:'0 auto 12px', display:'block', opacity:0.3 }}/>
              {users.length === 0 ? 'No users yet — click Add User to create the first one.' : 'No users match your filters.'}
            </div>
          ) : (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
              {/* Table header */}
              <div style={{
                display:'grid', gridTemplateColumns:'1fr 1.4fr 1fr 0.7fr 0.8fr 0.8fr auto',
                padding:'10px 16px', borderBottom:`1px solid ${C.border}`,
                fontSize:10, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:C.muted,
              }}>
                <span>Name</span><span>Email</span><span>Role</span>
                <span>Portal</span><span>Status</span><span>Last Login</span><span>Actions</span>
              </div>

              {filtered.map((u, i) => (
                <div key={u.id} style={{
                  display:'grid', gridTemplateColumns:'1fr 1.4fr 1fr 0.7fr 0.8fr 0.8fr auto',
                  padding:'12px 16px', alignItems:'center', gap:8,
                  borderBottom: i < filtered.length-1 ? `1px solid ${C.border}` : 'none',
                  background: !u.active ? 'rgba(239,68,68,0.03)' : 'transparent',
                }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{u.name}</div>
                    {u.firstLogin && <div style={{ fontSize:10, color:C.amber }}>⚠️ First login pending</div>}
                  </div>
                  <div style={{ fontSize:12, color:C.muted, wordBreak:'break-all' }}>{u.email}</div>
                  <div><RoleBadge role={u.systemRole || u.role || 'VIEWER'}/></div>
                  <div style={{ fontSize:11, color:C.muted }}>{u.portal || '—'}</div>
                  <div>
                    <span style={{
                      fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10,
                      background: u.active ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                      color: u.active ? '#22c55e' : C.red,
                    }}>
                      {u.active ? '● Active' : '● Inactive'}
                    </span>
                  </div>
                  <div style={{ fontSize:11, color:C.muted }}>{fmt(u.lastLogin)}</div>

                  {/* Action buttons */}
                  <div style={{ display:'flex', gap:4 }}>
                    <button title="Edit user" onClick={() => openEdit(u)} style={{
                      background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.2)',
                      borderRadius:6, padding:'4px 7px', cursor:'pointer', color:C.blue,
                    }}><Edit2 size={12}/></button>
                    <button title="Reset password" onClick={() => { setResetUser(u); setNewPw(''); }} style={{
                      background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.2)',
                      borderRadius:6, padding:'4px 7px', cursor:'pointer', color:C.amber,
                    }}><Key size={12}/></button>
                    <button title={u.active ? 'Deactivate' : 'Activate'} onClick={() => handleToggleActive(u)} style={{
                      background: u.active ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                      border: u.active ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(34,197,94,0.2)',
                      borderRadius:6, padding:'4px 7px', cursor:'pointer',
                      color: u.active ? C.red : '#22c55e',
                    }}>
                      {u.active ? <PowerOff size={12}/> : <Power size={12}/>}
                    </button>
                    {u.email !== 'erueuzairue@gmail.com' && (
                      <button title="Delete user" onClick={() => setDeleteUser(u)} style={{
                        background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)',
                        borderRadius:6, padding:'4px 7px', cursor:'pointer', color:C.red,
                      }}><Trash2 size={12}/></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* RBAC Reference card */}
          <div style={{ marginTop:24, background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:16 }}>
            <h3 style={{ fontFamily:'Cinzel, serif', fontSize:13, color:C.gold, marginBottom:12 }}>
              🔐 Role Reference — Permissions by Role
            </h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:8 }}>
              {Object.entries(PORTAL_ROLES).map(([portal, group]) => (
                <div key={portal} style={{ background:'#0a1410', borderRadius:8, padding:10 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:C.gold, marginBottom:6 }}>{group.label}</div>
                  {group.roles.map(r => (
                    <div key={r.value} style={{ marginBottom:4 }}>
                      <RoleBadge role={r.value}/>
                      <div style={{ fontSize:10, color:C.dim, marginTop:2, paddingLeft:4 }}>{r.desc}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── SESSIONS TAB ── */}
      {tab === 'sessions' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontSize:13, color:C.muted }}>
              {sessions.length === 0 ? 'No active sessions detected.' : `${sessions.length} active session(s)`}
            </div>
            <button style={btnStyle('ghost')} onClick={loadSessions}><RefreshCw size={13}/> Refresh</button>
          </div>

          {sessions.length === 0 ? (
            <div style={{ textAlign:'center', padding:60, color:C.dim }}>
              <Clock size={32} style={{ margin:'0 auto 12px', display:'block', opacity:0.3 }}/>
              <p>No active sessions to display.</p>
              <p style={{ fontSize:12 }}>Sessions appear here when users are logged in.</p>
            </div>
          ) : (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
              {sessions.map((s, i) => (
                <div key={s.id} style={{
                  display:'grid', gridTemplateColumns:'1.5fr 1fr 1fr 1fr auto',
                  padding:'12px 16px', alignItems:'center',
                  borderBottom: i < sessions.length-1 ? `1px solid ${C.border}` : 'none',
                }}>
                  <div style={{ fontSize:13, color:C.text }}>{s.email}</div>
                  <RoleBadge role={s.role}/>
                  <div style={{ fontSize:11, color:C.muted }}>{fmt(s.loginAt)}</div>
                  <div style={{ fontSize:11, color:C.muted }}>{s.ip || 'Unknown IP'}</div>
                  <button style={btnStyle('danger')} onClick={() => handleForceLogout(s.id, s.email)}>
                    <LogOut size={12}/> Force Logout
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Session policy note */}
          <div style={{
            marginTop:20, padding:'12px 16px', borderRadius:10,
            background:'rgba(96,165,250,0.06)', border:`1px solid rgba(96,165,250,0.2)`,
            fontSize:12, color:C.muted,
          }}>
            <strong style={{ color:C.blue }}>Session Policy:</strong> JWT tokens expire after 24 hours automatically.
            Force logout terminates a session immediately. Users must re-login after a forced logout.
          </div>
        </div>
      )}

      {/* ── MODALS ── */}

      {/* Add User Modal */}
      {showAdd && (
        <Modal title="➕ Add New User" onClose={() => { setShowAdd(false); setForm(emptyForm()); }}>
          <UserForm/>
        </Modal>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal title={`✏️ Edit User — ${editUser.name}`} onClose={() => { setEditUser(null); setForm(emptyForm()); }}>
          <UserForm isEdit/>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <Modal title={`🔑 Reset Password — ${resetUser.name}`} onClose={() => { setResetUser(null); setNewPw(''); }}>
          <p style={{ fontSize:13, color:C.muted, marginBottom:16 }}>
            Set a new password for <strong style={{ color:C.text }}>{resetUser.email}</strong>.
            The user will be prompted to change it on next login.
          </p>
          <Field label="New Password" required>
            <div style={{ position:'relative' }}>
              <input style={{...inputStyle, paddingRight:36}} type={showPw ? 'text' : 'password'}
                value={newPw} placeholder="Min. 6 characters"
                onChange={e => setNewPw(e.target.value)} />
              <button type="button" onClick={() => setShowPw(p=>!p)} style={{
                position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                background:'none', border:'none', color:C.muted, cursor:'pointer',
              }}>
                {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
              </button>
            </div>
          </Field>
          <div style={{ display:'flex', gap:8, marginTop:16, justifyContent:'flex-end' }}>
            <button style={btnStyle('ghost')} onClick={() => { setResetUser(null); setNewPw(''); }}>Cancel</button>
            <button style={btnStyle('amber')} onClick={handleReset} disabled={saving}>
              {saving ? <Loader2 size={13} style={{ animation:'spin 1s linear infinite' }}/> : <Key size={13}/>}
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      {deleteUser && (
        <Modal title="⚠️ Delete User — Confirm" onClose={() => setDeleteUser(null)}>
          <div style={{ textAlign:'center', padding:'8px 0 20px' }}>
            <AlertTriangle size={36} style={{ color:C.red, margin:'0 auto 12px', display:'block' }}/>
            <p style={{ fontSize:14, color:C.text, marginBottom:6 }}>
              Delete <strong>{deleteUser.name}</strong>?
            </p>
            <p style={{ fontSize:12, color:C.muted, marginBottom:20 }}>
              {deleteUser.email} · {deleteUser.systemRole || deleteUser.role}<br/>
              This action cannot be undone.
            </p>
            <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
              <button style={btnStyle('ghost')} onClick={() => setDeleteUser(null)}>Cancel</button>
              <button style={btnStyle('danger')} onClick={handleDelete} disabled={saving}>
                {saving ? <Loader2 size={13} style={{ animation:'spin 1s linear infinite' }}/> : <Trash2 size={13}/>}
                {saving ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
